import { useEffect, useState } from 'react';

import { fetchMe } from '../lib/api';

type AuthState = 'loading' | 'authenticated';

interface AuthGuardProps {
  children: React.ReactNode;
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const [state, setState] = useState<AuthState>('loading');

  useEffect(() => {
    let cancelled = false;

    async function verifySession() {
      const result = await fetchMe();
      if (cancelled) {
        return;
      }

      if (result.ok) {
        setState('authenticated');
        return;
      }

      if (result.status === 401) {
        window.location.href = '/api/v1/auth/login';
        return;
      }

      // Transient API/edge errors: retry once, then show the shell (pages surface their own errors).
      if (result.status === 0 || result.status >= 500) {
        const retry = await fetchMe();
        if (cancelled) {
          return;
        }
        if (retry.ok) {
          setState('authenticated');
          return;
        }
        if (retry.status === 401) {
          window.location.href = '/api/v1/auth/login';
          return;
        }
        setState('authenticated');
        return;
      }

      window.location.href = '/api/v1/auth/login';
    }

    void verifySession();

    return () => {
      cancelled = true;
    };
  }, []);

  if (state === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zt-bg text-slate-300">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-zt-border border-t-sky-400" />
          <p className="text-sm">Checking session…</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
